/**
 * @file Decides when a call that looks alive has stopped carrying our audio (#2223 AC13, AC23–AC26).
 *
 * Found on a real Android Chrome Wi-Fi→4G switch: LiveKit resumed over the
 * new network, the server dropped every candidate on the new path, and the
 * client stayed `connected` -- no reconnecting pill -- while no audio went
 * either way for 67 s. LiveKit's own safety nets did not fire.
 *
 * "Flowing" means the server confirms it receives our mic: the
 * `remote-inbound-rtp` stat of our mic's `outbound-rtp`, which the browser
 * updates on every RTCP receiver report. The SFU sends one roughly every
 * second while it gets our packets -- a muted mic still sends silence -- and
 * none once our packets stop reaching it (measured in Chrome: ~1.0 s apart,
 * ~1.3 s while muted, none while nothing is sent).
 *
 * An earlier trigger used the bytes the publisher connection received. On a
 * prod call it rejoined an iPhone (Chrome iOS, carrier NAT) 8 times while
 * its audio flowed, so it is gone. A browser that exposes no receipts is
 * never judged by this check; only "LiveKit reconnecting too long" applies.
 * Incoming audio from others is deliberately not used: it also stops when
 * *their* connection dies, and that is not ours to fix.
 *
 * A pure reducer so the timing is testable without a browser.
 */
import { ConnectionState } from "livekit-client";

/**
 * No new receipt for this long while the mic is live = stalled. Reports come
 * ~1.0 s apart (~1.3 s while muted), so one lost report makes a ~2.6 s gap;
 * 2.8 s tolerates it. A false rejoin of someone whose audio flows is worse
 * than ~0.8 s more silence on a real stall (user decision 2026-11-04, AC23
 * over AC24).
 */
export const RECEIPT_STALL_MS = 2800;
/** LiveKit resuming for this long = stalled; a fresh connection is faster than a long resume. */
export const RECONNECTING_STALL_MS = 2000;
/**
 * After a network change, no new receipt for this long = stalled. Not 1 s:
 * reports come ~1.0 s apart (~1.3 s while muted), so a 1 s window would often
 * close just before a healthy report arrives.
 */
export const PROBE_MS = 1500;
/**
 * Samples come from a 1 s timer plus an async stats read, so "2 s since the
 * last receipt" is measured as e.g. 1999 ms and would slip a whole sample, to
 * 3 s. Thresholds are met this much early instead.
 */
const TIMER_JITTER_MS = 100;

export interface StallSample {
  state: ConnectionState;
  /** A published mic whose capture is running (not ended, not interrupted by the OS). */
  micLive: boolean;
  /** A value that changes with every server receipt of our audio; null if the browser exposes none. */
  receipt: number | null;
}

export interface StallState {
  /** When LiveKit's own reconnect began; null when not reconnecting. */
  reconnectingSince: number | null;
  /** When a new receipt was last seen. */
  progressAt: number | null;
  lastReceipt: number | null;
  /** When a network change asked for a fast check; null when none is pending. */
  probeAt: number | null;
}

export const initialStall = (): StallState => ({
  reconnectingSince: null,
  progressAt: null,
  lastReceipt: null,
  probeAt: null,
});

const RECONNECTING = new Set<ConnectionState>([
  ConnectionState.Reconnecting,
  ConnectionState.SignalReconnecting,
]);

export function nextStall(
  prev: StallState,
  sample: StallSample,
  now: number,
): { state: StallState; stalled: boolean } {
  if (RECONNECTING.has(sample.state)) {
    const since = prev.reconnectingSince ?? now;
    return {
      state: { ...initialStall(), reconnectingSince: since },
      stalled: now - since >= RECONNECTING_STALL_MS - TIMER_JITTER_MS,
    };
  }
  if (sample.state !== ConnectionState.Connected) return { state: initialStall(), stalled: false };

  const receipt = sample.receipt;
  if (!sample.micLive || receipt === null) return { state: initialStall(), stalled: false };
  if (prev.lastReceipt === null || prev.progressAt === null || receipt !== prev.lastReceipt) {
    return { state: { ...initialStall(), progressAt: now, lastReceipt: receipt }, stalled: false };
  }
  const probeExpired = prev.probeAt !== null && now - prev.probeAt >= PROBE_MS - TIMER_JITTER_MS;
  return { state: prev, stalled: probeExpired || now - prev.progressAt >= RECEIPT_STALL_MS - TIMER_JITTER_MS };
}

/**
 * A network change happened: from now on a new receipt must arrive within
 * PROBE_MS. Only armed while receipts are being tracked (connected, mic live,
 * browser exposes them); call it right after a fresh sample so a receipt that
 * came before the change does not count as one after it.
 */
export function armProbe(state: StallState, now: number): StallState {
  if (state.lastReceipt === null || state.probeAt !== null) return state;
  return { ...state, probeAt: now };
}

/**
 * The receipt marker for the mic, read from its sender's stats report: the
 * `remote-inbound-rtp` its `outbound-rtp` points at. Prefers a count of
 * reports, then of RTT measurements (Chrome), then the stat's timestamp.
 */
export function receiptFrom(report: RTCStatsReport | undefined): number | null {
  if (!report) return null;
  let outbound: { id: string; remoteId?: string } | undefined;
  report.forEach((s) => {
    if (s.type === "outbound-rtp" && (s.kind ?? s.mediaType) === "audio") outbound = s;
  });
  if (!outbound) return null;
  let remote: Record<string, unknown> | undefined = outbound.remoteId ? report.get(outbound.remoteId) : undefined;
  if (!remote) {
    report.forEach((s) => {
      if (s.type === "remote-inbound-rtp" && s.localId === outbound!.id) remote = s;
    });
  }
  if (!remote) return null;
  for (const key of ["reportsReceived", "roundTripTimeMeasurements", "timestamp"]) {
    if (typeof remote[key] === "number") return remote[key] as number;
  }
  return null;
}
